/**
 * Shots — the AI module's entries in the capture harness.
 *
 * Every shot resets the fight to a fixed seed, places the player, spawns a
 * squad at hand-picked positions and then advances *only* the AI simulation a
 * fixed number of steps. Same seed, same frame, every time.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';

const _euler = new THREE.Euler();
const _v = new THREE.Vector3();

export function registerAIShots(ctx, api) {
  /**
   * Park the camera. The player system rewrites ctx.camera every frame from its
   * rig, so a shot has to move the rig, freeze the controller and pin the
   * transform — the same dance the physics module does.
   */
  function shotCam(c, from, at, fov, wantDebug) {
    look(c, from, at, fov);
    const dx = at[0] - from[0], dy = at[1] - from[1], dz = at[2] - from[2];
    const yaw = Math.atan2(-dx, -dz);
    const pitch = Math.atan2(dy, Math.hypot(dx, dz));
    _v.set(from[0], from[1], from[2]);
    api._claimShotCam(_v, wantDebug);

    const P = c.get?.('player');
    if (!P) return;
    try { if (typeof P.freeze === 'function') P.freeze(true); } catch { /* ignore */ }
    try {
      if (P.rig) {
        P.rig.eye.set(from[0], from[1], from[2]);
        P.rig.fov = fov;
        P.rig.quaternion.setFromEuler(_euler.set(pitch, yaw, 0, 'YXZ'));
        P.rig.applyTo(c);
      }
    } catch { /* look() above is the fallback */ }
  }

  /** Put the player somewhere for the fight without moving the camera there. */
  function placePlayer(c, feet, yaw) {
    const P = c.get?.('player');
    if (!P) return;
    try { P.place(new THREE.Vector3(feet[0], feet[1], feet[2]), yaw, 0); } catch { /* ignore */ }
    try { P.freeze(true); } catch { /* ignore */ }
  }

  const groundY = (x, z, near = 0) => {
    const y = api.nav.ready ? api.nav.floorY(x, z, near) : NaN;
    return Number.isFinite(y) ? y : 0;
  };

  /** Spawn a squad at explicit XZ positions, snapped to the navmesh. */
  function squadAt(list, opts = {}) {
    const pts = list.map(([x, z]) => new THREE.Vector3(x, groundY(x, z, opts.y ?? 0), z));
    return api.spawnSquad({ count: pts.length, points: pts, seed: opts.seed ?? 0x51A1, difficulty: opts.difficulty });
  }

  /* ---------------------------------------------------------------- *
   * 1. the bake
   * ---------------------------------------------------------------- */
  registerShot('ai_navmesh', (c) => {
    api.resetFight(0xA1BA1E);
    if (api.debug) {
      api.debug.showNav = true;
      api.debug.showCover = true;
      api.debug.showLinks = true;
      api.debug.showPaths = false;
      api.debug.showPerception = false;
      api.debug.showLabels = false;
      api.debug.update([], [], api.nav);
    }
    placePlayer(c, [-1, groundY(-1, -24), -24], 0);
    shotCam(c, [15.5, 19.0, 26.0], [-2.0, 0.8, 1.0], 46, true);
  }, { desc: 'baked navmesh + cover map over the market square, debug view on', settle: 6 });

  /* ---------------------------------------------------------------- *
   * 2. a squad mid-firefight
   * ---------------------------------------------------------------- */
  registerShot('ai_squad_engaged', (c) => {
    api.resetFight(0x5EED01);
    if (api.debug) api.debug.setEnabled(false);
    const py = groundY(0.0, -13.0);
    placePlayer(c, [0.0, py, -13.0], 0);
    squadAt([[-0.6, 2.4], [3.0, 1.0], [-3.4, 0.6], [6.2, 2.2], [1.2, 5.6]],
      { seed: 0x5EED01 });
    api.run(6.5);
    shotCam(c, [-9.0, 3.0, 2.5], [2.5, 1.0, 0.5], 62, false);
  }, { desc: 'a squad in cover mid-firefight, third person over the market square', settle: 4 });

  /* ---------------------------------------------------------------- *
   * 3. low and high cover
   * ---------------------------------------------------------------- */
  registerShot('ai_cover', (c) => {
    api.resetFight(0xC0FFEE);
    if (api.debug) api.debug.setEnabled(false);
    const py = groundY(0.5, -12.0);
    placePlayer(c, [0.5, py, -12.0], 0);
    // the dry fountain lip (low cover) and the stall row (high cover)
    squadAt([[0.4, 2.3], [3.0, 0.8], [-2.4, 1.2], [-8.6, -1.0]], { seed: 0xC0FFEE });
    api.run(6.5);
    shotCam(c, [-7.0, 2.4, 2.0], [2.0, 1.0, 1.0], 58, false);
  }, { desc: 'enemies using the fountain lip as low cover and the stalls as high cover', settle: 4 });

  /* ---------------------------------------------------------------- *
   * 4. a flank, with the assignment drawn
   * ---------------------------------------------------------------- */
  registerShot('ai_flank', (c) => {
    api.resetFight(0xF1A2C3);
    if (api.debug) {
      // the flank shot is about one decision — the baked layers only hide it
      api.debug.showNav = false;
      api.debug.showCover = false;
      api.debug.showLinks = false;
      api.debug.showPaths = true;
      api.debug.showPerception = true;
      api.debug.showLabels = true;
    }
    const py = groundY(-2.0, -12.0);
    placePlayer(c, [-2.0, py, -12.0], 0);
    squadAt([[-6.0, 6.5], [-1.0, 8.5], [4.0, 7.0], [8.5, 3.0], [9.5, -3.5]],
      { seed: 0xF1A2C3 });
    // long enough for the aggressor / flanker tickets to be handed out and for
    // the flanker to be a good way along its path
    api.run(9.0);
    shotCam(c, [-18.0, 12.0, -6.0], [2.0, 0.6, 4.0], 52, true);
  }, { desc: 'the flanker ticket: its assigned cover node and the path it is running', settle: 4 });

  /* ---------------------------------------------------------------- *
   * 5. death
   * ---------------------------------------------------------------- */
  registerShot('ai_death', (c) => {
    api.resetFight(0xDEAD01);
    if (api.debug) api.debug.setEnabled(false);
    const py = groundY(0.0, -12.0);
    placePlayer(c, [0.0, py, -12.0], 0);
    const sq = squadAt([[0.8, 1.8], [3.8, 0.4], [-2.6, 2.2]], { seed: 0xDEAD01 });
    api.run(4.5);
    /**
     * Put one down with a torso hit from the player's direction.
     *
     * Pick the man CLOSEST to the player: he is the one who pushed into the open, lit
     * half of the square, and the camera can then sit behind him on the player's side
     * with the fountain and the market behind the body instead of in front of it. The
     * two earlier heuristics (first in the list, then furthest from the fountain) both
     * chose men who died inside the basin or under a stall awning in deep shadow.
     */
    const PLAYER = new THREE.Vector3(0.0, 0, -12.0);
    const dPlayer = (m) => Math.hypot(m.pos.x - PLAYER.x, m.pos.z - PLAYER.z);
    const victim = sq.members.filter((m) => m.alive).sort((a, b) => dPlayer(a) - dPlayer(b))[0];
    if (victim) {
      _v.set(victim.pos.x - 0.0, 0.16, victim.pos.z - (-12.0)).normalize();
      victim.applyDamage(400, _v, 'chest');
    }
    /**
     * The ragdoll only advances when the ENGINE ticks, and the harness gives a shot a
     * dozen frames. At 0.23 s the body has barely started to go: the old frame showed a
     * man who still read as standing. Drive the fall here — 0.55 s of ragdoll plus the
     * 120 ms animation blend — so the capture lands on a body genuinely in the air,
     * carrying the momentum of the round that put it there.
     */
    const ch = victim && victim.character;
    if (ch && ch.anim) {
      for (let i = 0; i < 8; i++) ch.anim.update(1 / 60);
      if (ch.ragdoll && ch.ragdoll.step) for (let i = 0; i < 66; i++) ch.ragdoll.step(1 / 120);
      for (let i = 0; i < 4; i++) ch.anim.update(1 / 60);
    }
    // Frame where the body actually is now, from the side and low, so the fall is
    // legible against the market backdrop.
    let f = null;
    if (ch && ch.boneWorldPosition) f = ch.boneWorldPosition('chest', new THREE.Vector3());
    if (!f && victim) f = victim.pos.clone().setY(victim.pos.y + 1.0);
    if (f) {
      // Stand off toward the PLAYER, offset laterally for a three-quarter read. The
      // shooter's own side of the body is by construction an unobstructed line.
      const ox = PLAYER.x - f.x, oz = PLAYER.z - f.z;
      const l = Math.max(0.5, Math.hypot(ox, oz));
      const ux = ox / l, uz = oz / l;
      shotCam(c,
        [f.x + ux * 3.6 - uz * 1.6, f.y + 1.30, f.z + uz * 3.6 + ux * 1.6],
        [f.x, f.y - 0.05, f.z], 46, false);
    } else {
      shotCam(c, [-6.0, 2.1, 1.5], [0.6, 0.8, 0.6], 50, false);
    }
  }, { desc: 'an enemy mid death-ragdoll, its squad still fighting', settle: 8 });

  // Exposed so the framing scout (src/ai/scout.mjs) can re-aim a shot without
  // duplicating the player-rig pinning dance.
  api.shotCam = (from, at, fov, wantDebug) => shotCam(ctx, from, at, fov, wantDebug);
  api.placePlayer = (feet, yaw) => placePlayer(ctx, feet, yaw);
  api.groundY = groundY;

  void ctx;
}
